import React from 'react';

/**
 * MfaMethodSelector
 * -----------------
 * Purely-presentational UI for choosing an MFA enrollment method (Authenticator
 * app vs SMS). Shared by the service-user setup page (PatientSetUpMFA, all
 * environments) and the provider setup page (SetUpMFA) in dev/staging only —
 * provider production is authenticator-only and does not render this component.
 *
 * Why this exists
 * ---------------
 * Where a user may pick SMS or an authenticator app, centralising the choice
 * cards here (Recommended badge, stacked layout, reliability copy) keeps that
 * UX in one place — same idea as TotpEnrollmentPanel for the QR / OTP block.
 *
 * What this component does NOT own
 * --------------------------------
 * No Firebase, no enrollment state, no routing. The parent owns `selectedMethod`
 * (usually `null` until the user picks) and decides what happens next. This
 * component only renders the cards and calls `onSelect`.
 *
 * Design intent (business / UX)
 * -----------------------------
 * Authenticator apps are far more reliable than SMS. We therefore:
 *   1. Show Authenticator App first with a "Recommended" badge
 *   2. Show SMS second as a quieter secondary option (still fully available)
 * Copy stays plain-language for service users who may not be technical.
 * Neither option is pre-selected — the parent shows this after "Set up 2FA now".
 *
 * @param {object}   props
 * @param {'totp'|'sms'|null} props.selectedMethod  Currently selected method (or null).
 * @param {Function} props.onSelect                 Called with `'totp'` or `'sms'`.
 * @param {boolean}  [props.disabled=false]         Lock both cards (e.g. mid-flow).
 */
export default function MfaMethodSelector({
  selectedMethod = null,
  onSelect,
  disabled = false,
}) {
  // Hover/press treatment mirrors selectable cards elsewhere in the app
  // (e.g. Observation options, MDT plan choices): background wash, stronger
  // border, light shadow, and icon/label colour shift so the cards feel clickable
  // — not only a border change. Hover styles are omitted when disabled.
  const cardBase =
    'group w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ease-out';
  const selectedClasses = 'border-primary bg-primary/5 ring-1 ring-primary/20 shadow-sm';
  const unselectedClasses = disabled
    ? 'border-gray-200 bg-white shadow-sm opacity-60 cursor-not-allowed'
    : 'border-gray-200 bg-white shadow-sm cursor-pointer hover:border-primary/40 hover:bg-primary/5 hover:shadow-md hover:shadow-primary/5 active:scale-[0.99]';
  const selectedInteractive = disabled
    ? 'opacity-60 cursor-not-allowed'
    : 'cursor-pointer';

  return (
    <div className="flex flex-col gap-2.5 mb-5" role="group" aria-label="Verification method">
      {/* Authenticator App — primary / recommended option */}
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selectedMethod === 'totp'}
        onClick={() => onSelect('totp')}
        className={`${cardBase} ${selectedMethod === 'totp' ? `${selectedClasses} ${selectedInteractive}` : unselectedClasses}`}
      >
        {/* Shield icon */}
        <svg
          className={`w-6 h-6 shrink-0 mt-0.5 transition-colors duration-200 ${
            selectedMethod === 'totp'
              ? 'text-primary'
              : `text-gray-400${disabled ? '' : ' group-hover:text-primary'}`
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
          />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm font-semibold transition-colors duration-200 ${
                selectedMethod === 'totp'
                  ? 'text-primary'
                  : `text-gray-900${disabled ? '' : ' group-hover:text-primary'}`
              }`}
            >
              Authenticator App
            </span>
            {/* Text badge so "recommended" is not colour-only */}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-primary/10 text-primary">
              Recommended
            </span>
          </div>
          <p className={`text-xs text-gray-500 mt-0.5 leading-snug transition-colors duration-200${disabled ? '' : ' group-hover:text-gray-600'}`}>
            Codes work even if you have no signal or texts are delayed
          </p>
        </div>
      </button>

      {/* SMS — secondary option, still fully available */}
      <button
        type="button"
        disabled={disabled}
        aria-pressed={selectedMethod === 'sms'}
        onClick={() => onSelect('sms')}
        className={`${cardBase} ${selectedMethod === 'sms' ? `${selectedClasses} ${selectedInteractive}` : unselectedClasses}`}
      >
        {/* Phone icon */}
        <svg
          className={`w-5 h-5 shrink-0 mt-0.5 transition-colors duration-200 ${
            selectedMethod === 'sms'
              ? 'text-primary'
              : `text-gray-400${disabled ? '' : ' group-hover:text-primary'}`
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
          />
        </svg>
        <div className="min-w-0 flex-1">
          <span
            className={`text-sm font-medium transition-colors duration-200 ${
              selectedMethod === 'sms'
                ? 'text-primary'
                : `text-gray-700${disabled ? '' : ' group-hover:text-primary'}`
            }`}
          >
            SMS Text
          </span>
          <p className={`text-xs text-gray-500 mt-0.5 leading-snug transition-colors duration-200${disabled ? '' : ' group-hover:text-gray-600'}`}>
            You will get a code by text to your phone
          </p>
        </div>
      </button>
    </div>
  );
}
